import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listProfileActivity, mapApiActivityItemToUi, mapApiTabFilterToUi } from '../../../services/profileTabData.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_META = Object.freeze({
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: 0,
  hasNext: false,
  hasPrevious: false,
  nextCursor: null,
});

/**
 * The Activity feed of one record for the selected quick filter (CT-5527).
 *
 * Same shape as `useProfileTabData` — race guard, serialized filter keys,
 * memoized return — with the chip as one more input. The filter definitions come
 * back WITH the rows and are replaced on every response, because they differ
 * per chip: the hook never keeps an option list of its own.
 *
 * `focusEventId` ("Open in History", CT-5724) asks the server for the page that
 * holds that event; `focus` says whether it was found.
 */
export function useActivityFeed({
  entityType,
  entityId,
  tabId = 'activity',
  quickFilter = 'all',
  page = 1,
  pageSize = 10,
  orderBy,
  orderDirection,
  filters,
  search,
  focusEventId = null,
} = {}) {
  const [items, setItems] = useState([]);
  const [filterDefs, setFilterDefs] = useState([]);
  const [sortDefs, setSortDefs] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  // What the server answered about `focusEventId` ("Open in History", CT-5724):
  // `{ eventId, found }` for the request that asked, `null` otherwise.
  const [focus, setFocus] = useState(null);
  const fetchIdRef = useRef(0);

  // The VALUES drive a refetch, not the identity of an object literal.
  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  // `quiet` is the refresh after a write (a note was created or edited): the rows
  // already on screen stay until the new ones arrive, instead of collapsing into
  // a skeleton under a panel the user is still looking at.
  const fetchData = useCallback(async ({ quiet = false } = {}) => {
    if (!entityType || !entityId) return;
    const requestId = ++fetchIdRef.current;
    if (!quiet) {
      setLoading(true);
      setError(null);
      setStatus(null);
    }

    try {
      const parsedSearch = JSON.parse(searchKey);
      const { body, meta: rawMeta } = await listProfileActivity({
        entityType,
        entityId,
        tabId,
        quickFilter,
        page,
        pageSize,
        orderBy,
        orderDirection,
        filters: JSON.parse(filtersKey),
        ...(parsedSearch ? { search: parsedSearch } : {}),
        ...(focusEventId ? { focusEventId } : {}),
      });
      if (requestId !== fetchIdRef.current) return;
      setFocus(
        focusEventId
          ? { eventId: rawMeta?.focus_event_id ?? focusEventId, found: Boolean(rawMeta?.focus_found ?? rawMeta?.focusFound) }
          : null,
      );
      setItems((body?.items ?? []).map(mapApiActivityItemToUi));
      setFilterDefs((body?.filters ?? []).map(mapApiTabFilterToUi));
      setSortDefs(body?.sorts ?? []);
      setMeta(normalizePagedMeta(rawMeta) ?? EMPTY_META);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      // A failed QUIET refresh keeps what is on screen: the write itself worked.
      if (quiet) return;
      setItems([]);
      setFilterDefs([]);
      setSortDefs([]);
      setMeta(EMPTY_META);
      setFocus(focusEventId ? { eventId: focusEventId, found: false } : null);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the activity');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, tabId, quickFilter, page, pageSize, orderBy, orderDirection, filtersKey, searchKey, focusEventId]);

  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);
  const refetch = useCallback(() => fetchData(), [fetchData]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      items,
      filterDefs,
      sortDefs,
      meta,
      focus,
      loading,
      error,
      accessDenied: status === 403,
      notFound: status === 404,
      refetch,
      refresh,
    }),
    [items, filterDefs, sortDefs, meta, focus, loading, error, status, refetch, refresh],
  );
}

export default useActivityFeed;
